import {DatePipe} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import * as moment from 'moment';
import {Subscription} from 'rxjs';
import {Machine} from 'src/app/model/machine';
import {MachineLastValues} from 'src/app/model/custom/machine/machine-last-values';
import {MachinePeriodDataInfo} from 'src/app/model/custom/machine/machine-period-data-info';
import {Measure} from 'src/app/model/measure';
import {MachineDetailsService} from 'src/app/services/machine-details.service';
import {MachinesService} from 'src/app/services/machines.service';
import {AuthGuard} from "../../../../common/auth-guard.service";
import {MenuItem} from "primeng/api";
import {TranslocoService} from "@ngneat/transloco";
import {GenericGraphComponent} from "../../../shared/generic-graph/generic-graph.component";
import {TimezoneService} from "../../../../services/timezone.service";
import {ExportFileType} from "../../../../model/export-file-type";

export type Period = 'lastDay' | 'lastWeek' | 'lastMonth' | 'specificPeriod';

@Component({
  selector: 'app-machine-details-data',
  templateUrl: './machine-details-data.component.html',
  styleUrls: ['./machine-details-data.component.scss'],
  providers: [DatePipe]
})
export class MachineDetailsDataComponent implements OnInit, OnDestroy {

  @ViewChild('signalsChart')
  public signalsChart: GenericGraphComponent;

  public readonly PERMISSION_MANAGE_ADVANCED_MACHINES: string = 'PERMISSION_MANAGE_ADVANCED_MACHINES';

  public machine: Machine;
  public isMachineLoading: boolean = true;
  public isPeriodDataInfoLoading: boolean = true;
  public selectedMeasurements: boolean = false;

  public language: string;

  public availableYearRange: string;
  public tempSelectedPeriod: Period[] = ['lastDay'];
  public tempSelectedPeriodMeasurements: Period[] = ['lastDay'];
  public selectedPeriod: Period[] = ['lastDay'];
  public selectedPeriodMeasurements: Period[] = ['lastDay'];
  public isSpecificPeriodSelected: boolean = false;
  public isSpecificPeriodMeasurementsSelected: boolean = false;
  public tempSpecificPeriodRange: [any, any] = null;
  public tempSpecificPeriodRangeMeasurements: [any, any] = null;
  public specificPeriodRange: [any, any] = [null, null];
  public specificPeriodRangeMeasurements: [any, any] = [null, null];
  public isPeriodRangeValid: boolean = false;
  public isPeriodRangeMeasurementsValid: boolean = false;

  public filtersVisible: boolean = false;
  public filtersMeasurementsVisible: boolean = false;
  public filtersViewInLitersTemp: boolean = false;
  public filtersViewInLiters: boolean = false;

  public machineLastValues: MachineLastValues;
  public machinePeriodDataInfo: MachinePeriodDataInfo;

  public measurements: { label: string, value: string }[] = [];
  public selectedMeasurementsUuids: string[] = [];

  public exportVisible: boolean;
  public viewInLiters: boolean = false;
  public chartActions: MenuItem[];
  public exportFileType: ExportFileType;

  public squeezedValuesLastHourTranslation: string;
  public squeezedValuesLastDayTranslation: string;
  public squeezedValuesLastMonthTranslation: string;
  public totalSqueezedValuesInPeriodTranslation: string;
  public squeezedValuesByDayTranslation: string;
  public squeezedValuesByServiceHourTranslation: string;

  public graphMeasures: any;
  public graphAxis: any[];

  private readonly SPECIFIC_PERIOD: string = 'specificPeriod';

  private periodRange: [number, number] = [null, null];
  private periodRangeMeasurements: [number, number] = [null, null];

  private machineDetailsSubscription: Subscription;
  private measures: { [key: string]: Measure[] };
  private measurementsMap: Map<string, string> = new Map<string, string>();

  private exportChart;


  constructor(
    private readonly machineDetailsService: MachineDetailsService,
    private readonly machinesService: MachinesService,
    private readonly i18n: TranslocoService,
    private readonly timezoneService: TimezoneService,
    public authGuard: AuthGuard
  ) {
    this.chartActions = [];
  }

  ngOnInit(): void {
    this.language = this.i18n.getActiveLang();

    this.periodRange = this.getPeriodRange(this.selectedPeriod[0], this.isPeriodRangeValid);
    this.periodRangeMeasurements = this.getPeriodRange(this.selectedPeriodMeasurements[0], this.isPeriodRangeMeasurementsValid);
    this.availableYearRange = this.timezoneService.calcYearRange();
    this.loadTranslations();

    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      if (machineDetails.machineLoading === false) {
        this.machine = machineDetails.machine;
        this.getLastValues();
        this.loadFilters();
        this.getMachinePeriodDataInfo();
        this.getMachineMeasurements();
      }
    });
  }

  private loadTranslations(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.buildChartActions();
      this.loadLastValuesTranslations();
      this.loadPeriodDataInfoTranslations();
    });
  }

  private loadLastValuesTranslations(): void {
    this.squeezedValuesLastHourTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.data.squeezedLitersLastHour') :
      this.i18n.translate('misc.info.data.squeezedFruitsLastHour');
    this.squeezedValuesLastDayTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.data.squeezedLitersLastDay') :
      this.i18n.translate('misc.info.data.squeezedFruitsLastDay');
    this.squeezedValuesLastMonthTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.data.squeezedLitersLastMonth') :
      this.i18n.translate('misc.info.data.squeezedFruitsLastMonth');
  }

  private loadPeriodDataInfoTranslations(): void {
    this.totalSqueezedValuesInPeriodTranslation = this.filtersViewInLiters ?
      this.i18n.translate('misc.info.data.totalSqueezedLitersInPeriod') :
      this.i18n.translate('misc.info.data.totalSqueezedFruitsInPeriod');
    this.squeezedValuesByDayTranslation = this.filtersViewInLiters ?
      this.i18n.translate('misc.info.data.squeezedLitersByDay') :
      this.i18n.translate('misc.info.data.squeezedFruitsByDay');
    this.squeezedValuesByServiceHourTranslation = this.filtersViewInLiters ?
      this.i18n.translate('misc.info.data.squeezedLitersByServiceHour') :
      this.i18n.translate('misc.info.data.squeezedFruitsByServiceHour');
  }

  private buildChartActions(): void {
    this.chartActions = [
      {label: this.i18n.translate('common.exportXls'), icon: 'pi pi-file-excel', command: () => this.showExportToXls()},
      {label: this.i18n.translate('common.downloadPng'), icon: 'pi pi-download', command: () => this.downloadChart()}
    ];
  }

  showExportToXls(): void {
    this.exportVisible = true;
  }

  downloadChart(): void {
    try {
      setTimeout(() => {
        if (this.exportFileType === ExportFileType.MACHINE_DATA_CHART) {
          if (this.signalsChart.chart.axes != undefined) {
            this.exportChart = this.signalsChart.chart;
          }
          this.signalsChart.download(this.exportChart);
        }
      });
    } catch (ignored) {
    }
  }

  private getLastValues(): void {
    this.machinesService.getMachineLastValuesByUuid(this.machine.machineUuid, this.viewInLiters).subscribe(response => {
      this.machineLastValues = response.machineLastValues;
      this.isMachineLoading = false;
    });
  }

  private getMachinePeriodDataInfo(): void {
    this.machinesService.getMachinePeriodDataInfoByUuid(this.machine.machineUuid, this.periodRange[0], this.periodRange[1], this.filtersViewInLiters).subscribe(response => {
      this.machinePeriodDataInfo = response.machineDataInfo;
      this.isPeriodDataInfoLoading = false;
    });
  }

  private getMachineMeasurements(): void {
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)) {
      this.selectedMeasurements = (this.selectedMeasurementsUuids.length > 0);
      this.machinesService.getMachineMeasurements(this.machine.machineUuid, this.periodRangeMeasurements[0],
        this.periodRangeMeasurements[1], this.selectedMeasurementsUuids).subscribe(response => {
        this.measures = response.measures;
        let graphMeasures = [];
        Object.entries(this.measures).forEach((entry, _) => {
          graphMeasures.push({
            name: this.measurementsMap.get(entry[0]),
            data: entry[1].map(measure => {
              return [moment(measure.timestamp).utc().valueOf(), parseFloat(measure.value)]
            }),
            yAxis: 0
          })
        });

        this.graphAxis = [{labels: {format: '{value}'}, title: {enabled: false}}];
        graphMeasures.sort((a, b) => a.name > b.name ? 1 : -1);
        this.graphMeasures = graphMeasures;
      });
    }
  }

  changeLiters(event: any): void {
    this.viewInLiters = event.checked;
    this.isMachineLoading = true;
    this.i18n.selectTranslation().subscribe(() => {
      this.loadLastValuesTranslations();
      this.getLastValues();
    });
  }

  public selectPeriod(period: Period): void {
    this.tempSelectedPeriod = [period];
  }

  public selectPeriodMeasurements(period: Period): void {
    this.tempSelectedPeriodMeasurements = [period];
  }

  public applySelection(): void {
    this.isPeriodDataInfoLoading = true;
    this.filtersViewInLiters = this.filtersViewInLitersTemp;
    this.selectedPeriod = this.tempSelectedPeriod;
    this.isSpecificPeriodSelected = this.tempSelectedPeriod[0] === this.SPECIFIC_PERIOD;
    if (!this.isSpecificPeriodSelected) {
      this.periodRange = this.getPeriodRange(this.selectedPeriod[0], this.isPeriodRangeValid);
    } else {
      this.specificPeriodRange[0] = this.tempSpecificPeriodRange[0];
      this.specificPeriodRange[1] = this.tempSpecificPeriodRange[1];
      this.periodRange = this.getPeriodRange(this.selectedPeriod[0], this.isPeriodRangeValid, this.specificPeriodRange);
    }
    this.i18n.selectTranslation().subscribe(() => {
      this.loadPeriodDataInfoTranslations();
      this.getMachinePeriodDataInfo();
    });
  }

  public applyFilters(): void {
    this.graphMeasures = null;
    this.selectedMeasurements = (this.selectedMeasurementsUuids.length > 0);
    this.selectedPeriodMeasurements = this.tempSelectedPeriodMeasurements;
    this.isSpecificPeriodMeasurementsSelected = this.tempSelectedPeriodMeasurements[0] === this.SPECIFIC_PERIOD;
    if (!this.isSpecificPeriodMeasurementsSelected) {
      this.periodRangeMeasurements = this.getPeriodRange(this.selectedPeriodMeasurements[0], this.isPeriodRangeMeasurementsValid);
    } else {
      this.specificPeriodRangeMeasurements[0] = this.tempSpecificPeriodRangeMeasurements[0];
      this.specificPeriodRangeMeasurements[1] = this.tempSpecificPeriodRangeMeasurements[1];
      this.periodRangeMeasurements = this.getPeriodRange(this.selectedPeriodMeasurements[0], this.isPeriodRangeMeasurementsValid,
        this.specificPeriodRangeMeasurements);
    }
    this.getMachineMeasurements();
  }

  private getPeriodRange(period: Period, periodRangeValid: boolean, periodRange?: [any, any]): [number, number] {
    return this.timezoneService.getPeriodRange(period, periodRangeValid, periodRange);
  }

  public validatePeriodRange(specificPeriodRange: [any, any]): void {
    this.tempSpecificPeriodRange = specificPeriodRange;
    this.isPeriodRangeValid = specificPeriodRange?.[0] != null && specificPeriodRange?.[1] != null;
  }

  public validatePeriodRangeMeasurements(specificPeriodRange: [any, any]): void {
    this.tempSpecificPeriodRangeMeasurements = specificPeriodRange;
    this.isPeriodRangeMeasurementsValid = specificPeriodRange?.[0] != null && specificPeriodRange?.[1] != null;
  }

  private loadFilters(): void {
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)) {
      this.machinesService.getAllMachineMeasurements(this.machine.machineUuid).subscribe(response => {
        let tempMeasurements: any[] = [];
        let tempMeasurementsMap: any[] = [];
        Object.entries(response.measurements).forEach((entry) => {
          tempMeasurementsMap.push([entry[0], entry[1]]);
          tempMeasurements.push({label: entry[1], value: entry[0]});
        });
        tempMeasurements.sort((a, b) => a.label > b.label ? 1 : -1);
        this.measurements = tempMeasurements;
        this.measurementsMap = new Map<string, string>(tempMeasurementsMap);
      });
    }
  }

  public resetFilters(): void {
    this.tempSelectedPeriodMeasurements = ['lastDay'];
    this.selectedMeasurementsUuids = [];
    this.graphAxis = [];
    this.graphMeasures = [];
    this.selectedMeasurements = false;
  }

  public resetSelection(): void {
    this.tempSelectedPeriod = ['lastDay'];
  }

  public getContentExport() {
    if (this.exportFileType === ExportFileType.MACHINE_DATA) {
      return [this.machineLastValues, this.machinePeriodDataInfo, this.periodRange, this.viewInLiters, this.filtersViewInLiters];
    } else {
      return this.graphMeasures;
    }
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
  }

  public readonly ExportFileType = ExportFileType;
}
